import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { waLink } from "@/lib/whatsapp";
import { getContent, toPascal } from "@/lib/cms";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { ArrowRight, MessageCircle, Leaf, Sparkles } from "lucide-react";
import { LeafBranch, CitrusSprig, HerbSprig } from "@/components/WellnessDecor";

function Section({ title, subtitle, to, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          {subtitle && <span className="text-terracotta font-500 text-sm uppercase tracking-wider">{subtitle}</span>}
          <h2 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mt-1">{title}</h2>
        </div>
        {to && <Link to={to} className="hidden sm:inline-flex items-center gap-1.5 text-forest font-500 hover:text-terracotta transition-colors shrink-0">View all <ArrowRight className="w-4 h-4" /></Link>}
      </div>
      {children}
    </section>
  );
}

function Row({ products }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}

export default function Home() {
  const { settings } = useApp();
  const content = getContent(settings);
  const hero = content.hero || {};
  const [cats, setCats] = useState([]);
  const [goals, setGoals] = useState([]);
  const [products, setProducts] = useState({ trending: [], best_sellers: [], arrivals: [] });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/wellness-goals").then((r) => setGoals(r.data)).catch(() => {});
    Promise.all([
      api.get("/products?section=trending&limit=8").then((r) => r.data.items).catch(() => []),
      api.get("/products?section=best_sellers&limit=8&sort=popular").then((r) => r.data.items).catch(() => []),
      api.get("/products?sort=newest&limit=4").then((r) => r.data.items).catch(() => []),
      api.get("/products?sort=discount&limit=4").then((r) => r.data.items).catch(() => []),
    ]).then(([trending, best_sellers, arr, deals]) => {
      setProducts({ trending, best_sellers, arrivals: [...arr, ...deals].slice(0, 8) });
    });
  }, []);

  const sections = (content.sections || []).filter((s) => s.enabled);

  const renderSection = (s) => {
    switch (s.type) {
      case "categories":
        return (
          <Section key={s.id} title={s.title} subtitle={s.subtitle} to="/categories">
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3 lg:gap-4">
              {cats.slice(0, 14).map((c) => (
                <Link key={c.id} to={`/shop?category=${c.id}`} data-testid={`home-cat-${c.id}`}
                  className="group bg-white rounded-2xl p-3 lg:p-4 text-center soft-shadow hover:soft-shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-sand">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <span className="text-xs lg:text-sm font-500 text-ink leading-tight block">{c.name}</span>
                </Link>
              ))}
            </div>
          </Section>
        );
      case "wellness_goals":
        return (
          <section key={s.id} id="wellness-goals" style={{ backgroundColor: "#FFD400" }}>
            <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20">
              <span className="font-600 text-sm uppercase tracking-wider" style={{ color: "#1f2937" }}>{s.subtitle}</span>
              <h2 className="font-heading text-3xl lg:text-4xl mt-1 mb-8" style={{ color: "#111827" }}>{s.title}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
                {goals.map((g, i) => {
                  const Icon = Icons[toPascal(g.icon)] || Sparkles;
                  const badge = i % 2 === 0 ? "#166534" : "#EA580C";
                  return (
                    <Link key={g.id} to={`/wellness/${g.slug}`} data-testid={`goal-${g.id}`}
                      className="group bg-white hover:-translate-y-1 rounded-2xl p-5 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-all">
                      <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 shadow-inner" style={{ backgroundColor: badge }}>
                        <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                      </span>
                      <span className="text-sm font-600" style={{ color: "#1f2937" }}>{g.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        );
      case "products": {
        const list = products[s.source] || [];
        if (list.length === 0) return null;
        return <Section key={s.id} title={s.title} subtitle={s.subtitle} to="/shop"><Row products={list} /></Section>;
      }
      case "promo":
        return (
          <section key={s.id} className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="rounded-[32px] bg-gradient-to-r from-terracotta to-mustard p-8 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-6 overflow-hidden relative">
              <div className="relative z-10">
                <h3 className="font-heading text-3xl lg:text-5xl text-white tracking-tight">{s.title}</h3>
                <p className="text-white/90 mt-3 max-w-md">{s.subtitle}</p>
                {s.cta?.to && <Link to={s.cta.to}><Button className="mt-6 rounded-full h-12 px-8 bg-forest hover:bg-earth text-cream" data-testid="promo-deals-btn">{s.cta.label} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>}
              </div>
              <MessageCircle className="hidden lg:block w-40 h-40 text-white/15 absolute right-10" />
            </div>
          </section>
        );
      case "consultant_cta":
        return (
          <section key={s.id} className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
            <div className="rounded-[32px] bg-white soft-shadow p-8 lg:p-12 text-center relative overflow-hidden">
              <LeafBranch className="hidden sm:block absolute -left-4 -top-4 w-24 h-32 text-olive/20 rotate-6 pointer-events-none" />
              <CitrusSprig className="hidden sm:block absolute right-2 bottom-0 w-24 h-20 text-mustard/30 pointer-events-none" />
              <div className="relative z-10">
              <h3 className="font-heading text-3xl lg:text-4xl text-forest">{s.title}</h3>
              <p className="text-brown/70 mt-3 max-w-xl mx-auto">{s.subtitle}</p>
              <a href={waLink(settings.whatsapp_number, "Hi LIVENEAT Organic, I'd like to talk to a wellness consultant.")} target="_blank" rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-white rounded-full h-12 px-8 font-500 transition-colors" data-testid="home-wa-consultant-btn">
                <MessageCircle className="w-5 h-5" /> Chat with a Consultant
              </a>
              </div>
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-sand">
        <LeafBranch className="hidden md:block absolute -left-6 top-6 w-28 h-36 text-olive/25 rotate-12 pointer-events-none" />
        <HerbSprig className="hidden lg:block absolute right-[46%] bottom-2 w-24 h-32 text-forest/15 -rotate-12 pointer-events-none" />
        <CitrusSprig className="absolute left-2 bottom-4 w-24 h-20 text-mustard/40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center relative z-10">
          <div className="animate-fade-up">
            {hero.badge && <span className="inline-flex items-center gap-2 bg-forest/10 text-forest text-sm font-500 px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="w-4 h-4" /> {hero.badge}
            </span>}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tighter leading-[1.05]" data-testid="hero-headline">
              Good Choices.<br /><span className="text-green">Made Easy.</span>
            </h1>
            <p className="text-base lg:text-lg text-brown/80 mt-6 max-w-lg leading-relaxed">Healthy Food. Wellness Products. Expert Services. All in One Place.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={hero.primary_cta?.to || "/shop"}><Button data-testid="hero-shop-btn" className="rounded-full h-12 px-8 bg-orange hover:bg-[#e65f00] text-white text-base font-600 shadow-md">Explore Now <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to={hero.secondary_cta?.to || "/consultants"}><Button data-testid="hero-consultant-btn" variant="outline" className="rounded-full h-12 px-7 border-2 border-forest text-forest hover:bg-forest hover:text-cream text-base font-600">Find Wellness Near Me</Button></Link>
            </div>
            <div className="flex flex-wrap gap-6 mt-10 text-sm text-brown/70">
              {(hero.features || []).map((f, i) => {
                const Icon = Icons[toPascal(f.icon)] || Leaf;
                return <span key={i} className="inline-flex items-center gap-2"><Icon className="w-4 h-4 text-olive" /> {f.label}</span>;
              })}
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="aspect-[4/5] rounded-[32px] overflow-hidden soft-shadow-lg">
              <img src={hero.image} alt="Organic wellness lifestyle" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 bg-white rounded-2xl soft-shadow p-4 flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-mustard/30 flex items-center justify-center"><Sparkles className="w-5 h-5 text-terracotta" /></span>
              <div><div className="font-600 text-forest">{hero.card_title}</div><div className="text-xs text-muted-foreground">{hero.card_subtitle}</div></div>
            </div>
          </div>
        </div>
      </section>

      {sections.map(renderSection)}
    </div>
  );
}
